/**
 * Practice selection.
 *
 * The complaint this exists to fix: once you have learned something, being
 * asked it again is a waste of the session. A plain shuffle over the whole bank
 * does exactly that — the more you learn, the higher the proportion of
 * questions you already know.
 *
 * So questions are graded into tiers and drawn from the top down:
 *
 *   fresh      never asked. Always first — new material beats revision.
 *   weak       asked and got wrong. Second, because a known gap is the most
 *              valuable thing to spend a question on.
 *   shaky      right once. Worth one more pass to see if it stuck.
 *   mastered   right at least twice. Held back entirely unless the bank runs
 *              dry, and even then only the least recently seen come back.
 *
 * `MASTERY` is deliberately two rather than three: this is a Bible quiz, not a
 * language course, and asking someone the number of books in the Bible four
 * times is how an app gets deleted.
 */

import { QUESTIONS, XP_BY_DIFFICULTY } from '../data/questions.js';
import { presentQuestion } from './quiz.js';

export const MASTERY = 2;

/** @returns {'fresh'|'weak'|'shaky'|'mastered'} */
export function tierOf(record) {
  if (!record || !record.seen) return 'fresh';
  if ((record.right ?? 0) >= MASTERY) return 'mastered';
  if (!record.correct) return 'weak';
  return 'shaky';
}

const TIER_ORDER = ['fresh', 'weak', 'shaky', 'mastered'];

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function seededRandom(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle(items, rand) {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * How the bank breaks down for this player — drives the counts shown on the
 * practice cards, so a mode never promises questions it cannot deliver.
 */
export function practiceStats(answers = {}, pool = QUESTIONS) {
  const counts = { fresh: 0, weak: 0, shaky: 0, mastered: 0 };
  for (const question of pool) counts[tierOf(answers[question.id])] += 1;
  return {
    ...counts,
    total: pool.length,
    // Anything not yet mastered is still worth asking.
    remaining: counts.fresh + counts.weak + counts.shaky,
    progress: pool.length ? counts.mastered / pool.length : 0,
  };
}

/**
 * Build a practice set.
 *
 * @param {object}  options
 * @param {object}  options.answers    persisted quizAnswers
 * @param {number}  options.limit      how many questions
 * @param {string}  options.seed       stable ordering within a session
 * @param {string[]} [options.tiers]   restrict to these tiers (Weak Spots does)
 * @param {string[]} [options.categories]
 * @param {boolean} [options.allowMastered] fall back to mastered when short
 */
export function buildPracticeSet({
  answers = {},
  limit = 10,
  seed = 'practice',
  tiers = null,
  categories = null,
  allowMastered = true,
} = {}) {
  let pool = QUESTIONS;
  if (categories?.length) pool = pool.filter((q) => categories.includes(q.category));

  const buckets = { fresh: [], weak: [], shaky: [], mastered: [] };
  for (const question of pool) buckets[tierOf(answers[question.id])].push(question);

  const rand = seededRandom(hashString(seed));
  const wanted = tiers ?? TIER_ORDER;
  const picked = [];

  for (const tier of TIER_ORDER) {
    if (!wanted.includes(tier)) continue;
    if (tier === 'mastered' && !allowMastered && wanted !== tiers) continue;
    if (picked.length >= limit) break;

    let group = buckets[tier];
    // Mastered questions come back least-recently-seen first, so a forced
    // repeat is at least the one you have not seen for longest.
    if (tier === 'mastered') {
      group = [...group].sort(
        (a, b) => (answers[a.id]?.day ?? '').localeCompare(answers[b.id]?.day ?? ''),
      );
    } else {
      group = shuffle(group, rand);
    }
    picked.push(...group.slice(0, limit - picked.length));
  }

  return picked.map(presentQuestion);
}

/** XP for a question, with a speed bonus in timed mode. */
export function xpForAnswer(question, { secondsLeft = null, limit = null } = {}) {
  const base = XP_BY_DIFFICULTY[question.difficulty] ?? 10;
  if (secondsLeft == null || !limit) return base;
  // Up to half again for answering quickly. Rounded so the number shown is
  // always a whole one.
  const speed = Math.max(0, Math.min(1, secondsLeft / limit));
  return Math.round(base * (1 + speed * 0.5));
}
